import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { sendApi, templatesApi } from "@/api";
import Papa from "papaparse";
import { countSmsChars, SMS_MAX_CHARS, SMS_PREFIX } from "@/utils/sms";
import { Download } from "lucide-react";
import HelpTip from "@/components/ui/HelpTip";

function downloadCsvTemplate() {
  const bom = "\uFEFF";
  const content = bom + "phone,会社名,担当者名\n09012345678,○○商事,山田太郎\n08087654321,△△工業,佐藤花子\n";
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sms_bulk_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

type Step = "recipients" | "compose" | "confirm" | "done";

export default function BulkSend() {
  const [step, setStep] = useState<Step>("recipients");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [result, setResult] = useState<{ queued: number } | null>(null);

  const { data: templates } = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
  });

  const bulkMutation = useMutation({
    mutationFn: () =>
      sendApi.bulkSend({ recipients, body, templateId: templateId || undefined }),
    onSuccess: (data) => {
      setResult(data);
      setStep("done");
    },
  });

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const phones = results.data
          .map((row: any) => row.phone || row.tel || row["電話番号"] || "")
          .filter(Boolean);
        setRecipients(phones);
      },
    });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">一斉送信</h2>

      {/* ステップ表示 */}
      <div className="flex gap-2 text-sm">
        {(["recipients", "compose", "confirm", "done"] as Step[]).map((s, i) => (
          <span
            key={s}
            className={`px-3 py-1 rounded-full ${
              step === s ? "bg-blue-500 text-white" : "bg-slate-200 text-slate-600"
            }`}
          >
            {i + 1}. {s === "recipients" ? "宛先" : s === "compose" ? "本文" : s === "confirm" ? "確認" : "完了"}
          </span>
        ))}
      </div>

      {step === "recipients" && (
        <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">
                CSV ファイル（phone列を含む）
              </label>
              <button
                type="button"
                onClick={downloadCsvTemplate}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <Download size={14} />
                テンプレートCSV
              </button>
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvUpload}
              className="text-sm"
            />
          </div>
          {recipients.length > 0 && (
            <div>
              <p className="text-sm text-slate-600">
                {recipients.length}件の電話番号を読み込みました
              </p>
              <div className="mt-2 max-h-40 overflow-auto bg-slate-50 p-2 rounded text-xs font-mono">
                {recipients.slice(0, 20).map((p, i) => (
                  <div key={i}>{p}</div>
                ))}
                {recipients.length > 20 && (
                  <div className="text-slate-400">...他 {recipients.length - 20}件</div>
                )}
              </div>
            </div>
          )}
          <button
            onClick={() => setStep("compose")}
            disabled={recipients.length === 0}
            className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
          >
            次へ
          </button>
        </div>
      )}

      {step === "compose" && (
        <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">テンプレート（任意）</label>
            <select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                const tmpl = templates?.data.find((t) => t.id === e.target.value);
                if (tmpl) setBody(tmpl.body);
              }}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              <option value="">テンプレートなし（自由入力）</option>
              {templates?.data.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.slug} - {t.body.slice(0, 40)}...
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">メッセージ本文</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md text-sm ${
                countSmsChars(body).overLimit ? "border-red-400" : ""
              }`}
            />
            {body.length > 0 && (() => {
              const info = countSmsChars(body);
              return (
                <>
                  <div className={`flex justify-between text-xs mt-1 ${
                    info.overLimit ? "text-red-600" : info.remaining < 30 ? "text-orange-600" : "text-slate-500"
                  }`}>
                    <span>
                      展開見込み: 約{info.estimatedChars} / {SMS_MAX_CHARS}文字
                      {info.segments > 0 && `（${info.segments}通分）`}
                    </span>
                    <span>
                      {info.overLimit
                        ? `${-info.remaining}文字超過`
                        : `残り約${info.remaining}文字`}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {SMS_PREFIX} ({SMS_PREFIX.length}文字) が自動付与されます
                  </p>
                </>
              );
            })()}
            {countSmsChars(body).overLimit && (
              <p className="text-xs text-red-600 mt-1">
                展開見込みが{SMS_MAX_CHARS}文字を超えています。本文を短くしてください。
              </p>
            )}
            <p className="text-xs text-slate-400 mt-2">
              SMSは70文字で1通(3.3円)、以降67文字ごとに+1通課金。1文字超えるだけで通数が増えます。
              <HelpTip text="例: 71文字=2通(6.6円)、135文字=3通(9.9円)。プレフィックスも文字数に含まれます" />
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setStep("recipients")} className="px-4 py-2 border rounded-md text-sm">
              戻る
            </button>
            <button
              onClick={() => setStep("confirm")}
              disabled={!body.trim() || countSmsChars(body).overLimit}
              className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
            >
              確認へ
            </button>
          </div>
        </div>
      )}

      {step === "confirm" && (() => {
        const confirmInfo = countSmsChars(body);
        const totalCost = recipients.length * confirmInfo.segments * 3.3;
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
            {/* コスト概要カード */}
            <div className={`rounded-lg p-4 ${confirmInfo.segments >= 2 ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-200"}`}>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-slate-500">送信先</p>
                  <p className="text-2xl font-bold">{recipients.length}<span className="text-sm font-normal">件</span></p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">1件あたり</p>
                  <p className="text-2xl font-bold">{confirmInfo.segments}<span className="text-sm font-normal">通</span></p>
                  <p className="text-xs text-slate-400">{(confirmInfo.segments * 3.3).toFixed(1)}円/件</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">推定総額</p>
                  <p className={`text-2xl font-bold ${totalCost >= 1000 ? "text-red-600" : ""}`}>{totalCost.toLocaleString()}<span className="text-sm font-normal">円</span></p>
                </div>
              </div>
            </div>

            {/* 通数境界の注意 */}
            {confirmInfo.segments >= 2 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-800">
                <p className="font-medium">1件あたり{confirmInfo.segments}通分の課金です</p>
                <p className="text-xs mt-1">
                  送信文字数 {confirmInfo.estimatedChars}文字（プレフィックス{SMS_PREFIX.length}文字含む）。
                  SMSは70文字で1通、以降67文字ごとに1通追加で課金されます。
                  本文を{confirmInfo.estimatedChars - (confirmInfo.segments === 2 ? 71 : (confirmInfo.segments - 1) * 67 + 4)}文字短くすると{confirmInfo.segments - 1}通に抑えられます。
                </p>
              </div>
            )}

            <div className="text-sm space-y-2">
              <p>本文: 約{confirmInfo.estimatedChars}文字（{confirmInfo.segments}通分）</p>
              <p>プレビュー:</p>
              <div className="bg-slate-50 p-3 rounded text-sm">{SMS_PREFIX}{body}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep("compose")} className="px-4 py-2 border rounded-md text-sm">
                戻る
              </button>
              <button
                onClick={() => bulkMutation.mutate()}
                disabled={bulkMutation.isPending}
                className="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
              >
                {bulkMutation.isPending ? "送信中..." : `${totalCost.toLocaleString()}円で送信実行`}
              </button>
            </div>
          </div>
        );
      })()}

      {step === "done" && result && (
        <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
          <p className="text-green-600 font-medium">
            {result.queued}件のメッセージをキューに投入しました
          </p>
          <button
            onClick={() => {
              setStep("recipients");
              setRecipients([]);
              setBody("");
              setTemplateId("");
              setResult(null);
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm"
          >
            新規送信
          </button>
        </div>
      )}
    </div>
  );
}
